import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { FileText, Loader2, Play, RotateCw, Square, Terminal, Trash2 } from 'lucide-react';
import { customAdminApi } from '../../../services/api';
import type {
  CustomInstanceActualState,
  CustomInstanceLifecycleSummary,
  CustomInstancePreparationSummary,
  CustomInstanceDetail,
} from '../../../types/custom';
import { invalidateCustomInstanceQueries } from '../../../hooks/useCustomInstances';
import { customErrorMessage, newIdempotencyKey, operationInProgress } from '../../../utils/customUi';
import { CustomLogsModal } from './CustomLogsModal';
import { CustomConsoleModal } from './CustomConsoleModal';

export interface CustomInstanceActionTarget {
  id: string;
  name: string;
  actualState: CustomInstanceActualState;
  preparation: CustomInstancePreparationSummary;
  lifecycleOperation?: CustomInstanceLifecycleSummary;
  hasManagementConsole?: boolean;
}

interface CustomInstanceActionsProps {
  instance: CustomInstanceActionTarget;
  compact?: boolean;
  onAccepted?: (message: string) => void;
}

export function CustomInstanceActions({
  instance,
  compact = false,
  onAccepted,
}: CustomInstanceActionsProps) {
  const queryClient = useQueryClient();
  const [logsOpen, setLogsOpen] = useState(false);
  const [consoleInstance, setConsoleInstance] = useState<Pick<CustomInstanceDetail, 'id' | 'name' | 'managementConsole'> | null>(null);
  const consoleRequest = useMutation({
    // List rows carry only the declaration flag; resolve availability on explicit open.
    mutationFn: async () => {
      const { instance: detail } = await customAdminApi.getInstance(instance.id);
      return { id: detail.id, name: detail.name, managementConsole: detail.managementConsole };
    },
    onSuccess: setConsoleInstance,
    gcTime: 0,
  });

  const lifecycle = useMutation({
    mutationFn: (action: 'start' | 'stop' | 'restart' | 'delete') => customAdminApi.lifecycleInstance(
      instance.id,
      action,
      newIdempotencyKey(`instance-${action}`),
    ),
    onSuccess: async (result, action) => {
      onAccepted?.(`${action} accepted as operation ${result.operationId}. Persistent state will recover through REST.`);
      await invalidateCustomInstanceQueries(queryClient, instance.id);
    },
  });

  const activeOperation = operationInProgress(instance.lifecycleOperation?.phase);
  const pending = lifecycle.isPending || activeOperation;
  const prepared = instance.preparation.phase === 'agent_confirmed';
  const canStart = !pending && prepared && ['prepared', 'stopped', 'missing', 'error'].includes(instance.actualState);
  const canStop = !pending && ['running', 'starting', 'error'].includes(instance.actualState);
  const canRestart = !pending && instance.actualState === 'running';
  const canReadLogs = ['running', 'stopped', 'error'].includes(instance.actualState);
  const canOpenConsole = !pending && prepared && instance.actualState === 'running';
  const consoleTitle = pending ? 'Wait for the lifecycle operation to finish.'
    : !prepared ? 'Wait for instance preparation to finish.'
      : instance.actualState !== 'running' ? 'Start this instance before opening the console.' : 'Console';
  const buttonPadding = compact ? 'p-2' : 'py-1.5';

  const request = (action: 'start' | 'stop' | 'restart' | 'delete') => {
    if (action === 'delete' && !confirm(
      `Delete ${instance.name}? Its custom container and persistent data will be removed after Agent confirmation.`,
    )) return;
    lifecycle.mutate(action);
  };

  return <>
    <div className={`flex flex-wrap items-center gap-2 ${compact ? 'justify-end' : ''}`}>
      <button
        className={`btn-primary ${buttonPadding}`}
        disabled={!canStart}
        title="Start"
        aria-label={`Start ${instance.name}`}
        onClick={() => request('start')}
      ><Play className="w-4 h-4" />{!compact && ' Start'}</button>
      <button
        className={`btn-secondary ${buttonPadding}`}
        disabled={!canStop}
        title="Stop"
        aria-label={`Stop ${instance.name}`}
        onClick={() => request('stop')}
      ><Square className="w-4 h-4" />{!compact && ' Stop'}</button>
      <button
        className={`btn-secondary ${buttonPadding}`}
        disabled={!canRestart}
        title="Restart"
        aria-label={`Restart ${instance.name}`}
        onClick={() => request('restart')}
      ><RotateCw className="w-4 h-4" />{!compact && ' Restart'}</button>
      <button
        className={`btn-secondary ${buttonPadding}`}
        disabled={!canReadLogs}
        title="Docker Logs"
        aria-label={`Open Docker Logs for ${instance.name}`}
        onClick={() => setLogsOpen(true)}
      ><FileText className="w-4 h-4" />{!compact && ' Docker Logs'}</button>
      {instance.hasManagementConsole && <button
        className={`btn-secondary ${buttonPadding}`}
        disabled={!canOpenConsole || consoleRequest.isPending}
        title={consoleRequest.isPending ? 'Opening console…' : consoleTitle}
        aria-label={`Open Console for ${instance.name}`}
        onClick={() => consoleRequest.mutate()}
      >{consoleRequest.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Terminal className="w-4 h-4" />}{!compact && (consoleRequest.isPending ? ' Opening…' : ' Console')}</button>}
      <button
        className={`btn-danger ${buttonPadding}`}
        disabled={pending || instance.actualState === 'deleted'}
        title="Delete"
        aria-label={`Delete ${instance.name}`}
        onClick={() => request('delete')}
      ><Trash2 className="w-4 h-4" />{!compact && ' Delete'}</button>
    </div>
    {lifecycle.isError && <div className="mt-2 text-xs text-red-300">
      {customErrorMessage(lifecycle.error, 'The custom instance lifecycle request failed.')}
    </div>}
    {consoleRequest.isError && <div role="alert" className="mt-2 text-xs text-red-300">
      {customErrorMessage(consoleRequest.error, 'The custom instance console could not be opened.')}
    </div>}
    {logsOpen && <CustomLogsModal instance={instance} onClose={() => setLogsOpen(false)} />}
    {consoleInstance && <CustomConsoleModal instance={consoleInstance} onClose={() => setConsoleInstance(null)} />}
  </>;
}

export function CustomInstanceStatus({ status }: { status: CustomInstanceActualState }) {
  const className = status === 'running'
    ? 'badge-online'
    : (status === 'error'
      ? 'badge-error'
      : (['preparing', 'creating', 'starting', 'stopping', 'deleting'].includes(status)
        ? 'badge-warning'
        : 'badge-offline'));
  return <span className={className}>{status}</span>;
}
